import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../Admin/shared';
import { useFriendOverview } from './api/useFriendOverview';
import type { FriendTotals } from './api/types';
import { DateRangeControl, InfoTip, MetricKey, defaultRange } from '@/components/shared';
import { TrendChart } from './components/TrendChart';
import { DistributionBars } from './components/DistributionBars';
import { UserTable } from './components/UserTable';

/** `—` for a value we could not compute. Never `0` — that is a different claim. */
function fmtNum(v: number | null): string {
  return v === null ? '—' : v.toLocaleString();
}

function fmtPct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(1)}%`;
}

/** One stat tile, reading a single (independently-nullable) field. */
function Stat({
  label,
  value,
  hint,
  info,
}: {
  label: string;
  value: string;
  hint?: React.ReactNode;
  info?: MetricKey;
}) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">
        {label}
        {info && <InfoTip metric={info} />}
      </p>
      <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight">
        {value}
      </p>
      {hint && <p className="mt-1.5 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

/** The whole `totals` section failed server-side — named, so it is not read as zero. */
function UnavailableStat({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-700 tabular-nums">—</p>
      <p className="mt-1.5 text-[11px] text-amber-500/80">Not available</p>
    </div>
  );
}

function StatTiles({ totals }: { totals: FriendTotals | null }) {
  if (totals === null) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <UnavailableStat label="Total friendships" />
        <UnavailableStat label="Users with a friend" />
        <UnavailableStat label="Avg friends per user" />
        <UnavailableStat label="Avg friends per connected user" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Rows in `friends`, i.e. PAIRS — never "friendships per user". */}
      <Stat
        label="Total friendships (pairs)"
        value={fmtNum(totals.friendships_total)}
        hint="each row is one mutual pair"
      />
      <Stat
        label="Users with a friend"
        value={fmtNum(totals.users_with_friend)}
        hint={
          totals.pct_users_with_friend === null
            ? undefined
            : `${fmtPct(totals.pct_users_with_friend)} of users`
        }
      />
      <Stat
        label="Avg friends per user"
        info="friends_avg_per_user"
        value={fmtNum(totals.avg_friends_per_user)}
        hint="all users"
      />
      <Stat
        label="Avg friends per connected user"
        info="friends_avg_per_connected"
        value={fmtNum(totals.avg_friends_per_connected_user)}
        hint="users with at least one friend — stays legible on a sparse graph"
      />
    </div>
  );
}

/**
 * Friend analytics overview: date window, stat tiles, trend, distribution,
 * and the per-user table (which shares this page's date window and links out
 * to the per-user drill-down at `/analytics/friends/:userId`).
 */
export function FriendAnalyticsPage(): React.ReactElement {
  const [from, setFrom] = useState<string | null>(() => defaultRange()[0]);
  const [to, setTo] = useState<string | null>(() => defaultRange()[1]);

  const { data, loading, error } = useFriendOverview({
    from: from ?? undefined,
    to: to ?? undefined,
  });

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        {/* Same back-link treatment the per-user detail pages use, so moving
            up a level looks the same wherever you are in this area. */}
        <div>
          <Link
            to="/analytics"
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            ← Back to analytics
          </Link>
        </div>

        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-white tracking-tight">Friend Analytics</h1>
            <p className="mt-1 text-xs text-gray-500">
              How the social graph is growing and who is connected.
            </p>
          </div>
          <DateRangeControl from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        </header>

        {error && <ErrorBanner message={error} />}
        {loading && !data && (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        )}

        {data && (
          <>
            <StatTiles totals={data.totals} />

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Friendships formed</h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  New friend pairs per {data.series_granularity}.
                </p>
              </div>
              <div className="px-5 py-4">
                <TrendChart series={data.series} granularity={data.series_granularity} />
              </div>
            </section>

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Friend count distribution</h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  How many users fall into each friend-count band, right now.
                </p>
              </div>
              <div className="px-5 py-4">
                <DistributionBars distribution={data.distribution} />
              </div>
            </section>

            <UserTable from={from} to={to} />
          </>
        )}
      </div>
    </div>
  );
}

export default FriendAnalyticsPage;
